import {Box, Paper, Skeleton, Tab, Tabs, Typography} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import StaticDashboardWidgetGenerator from "../../../../operator/analytics/dashboard-v2/static-widgets";
import axios from "axios";
import DashboardWidget from "../../../../operator/analytics/dashboard-v2/components/dashboard-widget";
import {DateRangeButton} from "../../../../../../components/app-specific/date-range-controller";
import useQuery from "../../../../../../hooks/use-query";
import useStaticStatsWidgetsState, {STATIC_STATS_SAVED_STATE_KEYS,} from "../../../../operator/analytics/dashboard-v2/hooks/use-static-stats-widgets-state";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {NumberVisibilityModeProvider} from "../../../../operator/analytics/dashboard-v2/hooks/use-number-visibility-mode";
import NumberVisibilityModeToggle from "../../../../operator/analytics/dashboard-v2/components/number-visibility-mode-toggle";

/**
 * Vendor account statistics section — mirrors customer stats, powered by vendorInformationView widgets.
 */
const VendorStatisticsSection = ({loading: loadingProps, vendorCode, vendorName}) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.vendor-account-info.statistics'});
    const [loadingStats, setLoadingStats] = useState(true);
    const [_widgets, setWidgets] = useState([]);
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const canExportWidgets = Boolean(operatorAccessLevels.packages.dashboard.exportDashboardWidgets);

    const {
        loadingConfig,
        savingWidgetConfig,
        selectedCategory: savedSelectedCategory,
        setSelectedCategory: persistSelectedCategory,
        augmentCategorizedWidgets,
        saveWidgetOrderBy,
        saveWidgetConfig,
    } = useStaticStatsWidgetsState(STATIC_STATS_SAVED_STATE_KEYS.vendor);

    const widgets = useMemo(() => augmentCategorizedWidgets(_widgets), [_widgets, augmentCategorizedWidgets]);
    const widgetCategories = useMemo(() => widgets.map(e => e.category), [widgets]);
    const [localSelectedCategory, setLocalSelectedCategory] = useState(undefined);

    const selectedStatCategory = useMemo(() => {
        if (localSelectedCategory && widgetCategories.includes(localSelectedCategory)) {
            return localSelectedCategory;
        }
        if (savedSelectedCategory && widgetCategories.includes(savedSelectedCategory)) {
            return savedSelectedCategory;
        }
        return widgetCategories[0];
    }, [localSelectedCategory, savedSelectedCategory, widgetCategories]);

    const selectedWidgets = useMemo(
        () => widgets.filter(e => e.category === selectedStatCategory).flatMap((e) => e.widgets),
        [widgets, selectedStatCategory],
    );

    const loading = useMemo(
        () => loadingProps || loadingStats || loadingConfig,
        [loadingProps, loadingStats, loadingConfig],
    );

    /**
     * Updates the selected category locally and persists it.
     */
    const handleCategoryChange = useCallback((_, value) => {
        setLocalSelectedCategory(value);
        void persistSelectedCategory(value);
    }, [persistSelectedCategory]);

    /**
     * Fetches the vendor information widgets from the server.
     */
    const getVendorInformationWidgets = useCallback(async (cancelToken) => {
        setLoadingStats(true);
        const nextWidgets = await StaticDashboardWidgetGenerator.vendorInformationView(cancelToken);
        if (!isMounted())
            return;
        setWidgets(nextWidgets ?? []);
        if (!cancelToken?.reason)
            setLoadingStats(false);
    }, [isMounted]);

    /**
     * As soon as the component is mounted, fetches the widgets.
     */
    useEffect(() => {
        const cancelTokenSource = axios.CancelToken.source();
        void getVendorInformationWidgets(cancelTokenSource.token);
        return () => {
            cancelTokenSource.cancel();
        };
    }, [getVendorInformationWidgets]);

    const [query, setQuery] = useQuery();
    const filters = useMemo(() => ({
        ...query,
        // Vendor widgets filter by vendor code when provided by the backend widget config.
        vendorCode: vendorCode ?? undefined,
        vendorName: vendorName ?? undefined,
    }), [query, vendorCode, vendorName]);

    /**
     * Defaults the date range in the query when missing.
     */
    useEffect(() => {
        setQuery((prev) => ({
            ...prev,
            ...(!('dateRangeValue' in prev) && {
                dateRangeValue: {
                    mode: 'auto',
                    auto: {type: 'current', batch: 'year', count: 1, untilNow: true},
                },
            }),
        }), {replace: true});
    }, [setQuery]);

    return (
        <Paper sx={{padding: 2, display: 'flex', flexDirection: 'column', position: 'relative', height: '100%', gap: 3}}>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2}}>
                <Typography variant={'subtitle1'} fontWeight={600}>
                    {t('title')}
                </Typography>
                <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1}}>
                    <NumberVisibilityModeToggle/>
                    <DateRangeButton
                        disallowedTypes={['next']}
                        disallowedBatches={['hour']}
                        disallowFromNow
                        value={filters.dateRangeValue}
                        onCommitted={(value) => {
                            setQuery((prevState) => ({
                                ...prevState,
                                dateRangeValue: value,
                            }), {replace: true});
                        }}
                    />
                </Box>
            </Box>
            <Grid2 container spacing={2} sx={{width: '100%'}}>
                {
                    loading &&
                    Array.from({length: 8}).map((_, index) => (
                        <Grid2 xs={12} lg={6} key={index}>
                            <Skeleton height={350} sx={{transform: 'none'}}/>
                        </Grid2>
                    ))
                }
                {
                    !loading &&
                    <>
                        <Grid2 xs={12}>
                            <Tabs variant='standard' value={selectedStatCategory} onChange={handleCategoryChange}>
                                {
                                    widgetCategories.map((category) => (
                                        <Tab key={category} label={category} value={category}/>
                                    ))
                                }
                            </Tabs>
                        </Grid2>
                        {
                            selectedWidgets.map((widget) => (
                                <Grid2 {...widget?.size} key={widget.connectionId || widget.id}>
                                    <DashboardWidget
                                        simplified
                                        widget={widget}
                                        filters={filters}
                                        canViewWidgetFilters={false}
                                        canManageWidgetFilters={false}
                                        canExportWidgets={canExportWidgets}
                                        savingDashboardWidgetConfig={savingWidgetConfig}
                                        saveDashboardWidgetConfig={saveWidgetConfig}
                                        saveWidgetOrderBy={saveWidgetOrderBy}
                                    />
                                </Grid2>
                            ))
                        }
                    </>
                }
            </Grid2>
        </Paper>
    );
};

const VendorStatisticsSectionWithNumberVisibility = (props) => (
    <NumberVisibilityModeProvider>
        <VendorStatisticsSection {...props} />
    </NumberVisibilityModeProvider>
);

export default VendorStatisticsSectionWithNumberVisibility;
